import { useState, useEffect, useRef, useCallback, UIEvent, FC } from "react";
import { Params, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Modal, IconButton } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { ReactComponent as ArrowExpandIcon } from "../../assets/arrow-expand.icon.svg";
import { ChatEvents } from "../../common/enums/chat-events.enum";
import { SourceType } from "../../common/enums/source-type.enum";
import { Message } from "../../common/interfaces/chats/message.interface";
import { SummaryProps } from "../../common/interfaces/chats/summary.interface";
import { FeedbackProps } from "../../common/interfaces/chats/feedback.interface";
import { ToastContainer } from "../../components/toast/toast.comp";
import { ThemeContainer } from "../../common/containers/theme/theme.container";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { StatisticsContainer } from "../../common/containers/statistics/statistics.container";
import TivaDataModalView from "../tiva-data/tiva-data";
import { LoaderView } from "../../components/loader/loader.comp";
import {
    ChatBubble,
    ChatBubbleType,
    Feedback,
    ChatMessageReview,
    ChatMessageReviewBackdrop,
    SheetViewer,
    applyActivityEvent,
    emptyActivityFeed
} from "@mbassist/chat-surface";
import type { ActivityFeed, FeedEvent } from "@mbassist/chat-surface";
import "@mbassist/chat-surface/styles.css";
import {
    listenAnswers,
    addQuestion,
    fetchChatHistory,
    summarizeVideo,
    feedback,
    resolveChatPresignedUrl,
    resolveSheetExcerpt
} from "../../api/chats/endpoints-referrers";
import { isMobileDevice, isIOS } from "../../common/utilities/platform/platform.util";
import { AskUserOptions } from "../../components/ask-user-options/ask-user-options.comp";
import type { AskUserPrompt } from "../../components/ask-user-options/ask-user-options.comp";
import { useWindowDimensions } from "../../common/utilities/window-dimensions/window-dimensions.util";
import {
    stringGetter,
    objGetter
} from "../../common/utilities/localstorage-dealer/localstorage-getters.util";
import {
    setDataToLocalStorage,
    removeDataToLocalStorage
} from "../../common/utilities/localstorage-dealer/localstorage-setter.util";

interface SseMessage {
    id: number;
    error: any;
    answer: string;
    isStreaming: boolean;
    isWaiting: boolean;
    sessionId: string;
    answerId?: string;
    createdAt?: string;
    metadata?: any;
    question?: string;
    isRetry?: boolean;
    payload?: any;
    languageCode?: string;
    // Agentic typed stream event (backend eventBridge, plan P1-07/P1-08).
    // Present only on connections opened with ?events=typed.
    event?: {
        type: string;
        text?: string;
        seq?: number;
        [key: string]: unknown;
    };
}

export interface ChatMessage {
    id?: number;
    uid?: string;
    answerId?: string;
    text: string;
    data?: any;
    isLoading: boolean;
    type: ChatBubbleType;
    isExpanded?: boolean;
    metadata: any;
    feedback?: FeedbackProps;
    // BCP-47 language of an assistant answer (WCAG 3.1.2) → ChatBubble `lang`.
    lang?: string;
    // Agentic path (v2.1.0 P2-09/P3-06): grouped activity feed,
    // best-effort flag — threaded into ChatBubble.
    activityFeed?: ActivityFeed;
    bestEffort?: boolean;
    // Operational tool failures from payload.flags.toolErrors — rendered as
    // an error banner in the bubble.
    toolErrors?: string[];
    // P4-01c: a suspended turn's clarifying question. The bubble shows the
    // question text; option chips render under the LATEST turn only.
    askUser?: AskUserPrompt;
}

/**
 * P4-01c: the persisted payload of a suspended turn carries
 * { status: "awaiting_user", question: { question, options } } — from the
 * live SSE final message AND from reloaded history, so chips survive a
 * refresh while the suspension is still the latest turn.
 */
const askUserFromPayload = (payload: any): AskUserPrompt | undefined =>
    payload?.status === "awaiting_user" && payload?.question?.question
        ? (payload.question as AskUserPrompt)
        : undefined;

export interface ChatMessagePair {
    question?: ChatMessage;
    answer?: ChatMessage;
}

/**
 * Stable, collision-safe React key for a message pair. Using a fresh
 * crypto.randomUUID() as the key remounts every row (and every child) on each
 * render — catastrophic during answer streaming — so derive the key from an
 * identifier that persists across renders: the question's uid (set once when a
 * message is sent) or id (server history), else the answer's id. Sources are
 * prefixed so a question id can never collide with an answer id. Falls back to
 * the index only when nothing stable exists (messages are append-only, so the
 * index is itself stable).
 */
const getMessagePairKey = (message: ChatMessagePair, index: number): string => {
    if (message.question?.uid) return `q-${message.question.uid}`;
    if (message.question?.id != null) return `q-${message.question.id}`;
    if (message.answer?.answerId) return `a-${message.answer.answerId}`;
    if (message.answer?.id != null) return `a-${message.answer.id}`;
    return `row-${index}`;
};

export const ChatMessages = ({
    newMessage,
    setNewMessage,
    placeholder
}: {
    newMessage: string | null;
    setNewMessage: (arg1: string | null) => void;
    placeholder: FC<React.SVGProps<SVGSVGElement>>;
}) => {
    interface QuestionEvent {
        chatId: string;
        message: ChatMessage;
    }

    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { chatId } = useParams<Params<string>>();
    const { isTrial, user, currentAccessToken } = FirebaseAuthContainer.useContainer();
    const { isReady: isStatisticsReady } = StatisticsContainer.useContainer();
    const { devMode } = ThemeContainer.useContainer();
    const { showToast } = ToastContainer.useContainer();
    const { width } = useWindowDimensions();
    const chatScrollRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const reviewRef = useRef<HTMLDivElement>(null);
    const [messages, setMessages] = useState<ChatMessagePair[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeMessage, setActiveMessage] = useState<number>();
    const [reviewScroll, setReviewScroll] = useState<boolean>(false);
    const [reviewScrollTop, setReviewScrollTop] = useState<number>(0);
    const [reviewHeight, setReviewHeight] = useState<number>(0);
    const [isScrolledDown, setScrolledDown] = useState<boolean>(true);
    const availableScrollDownType = useRef<ScrollBehavior | null>(null);
    const [tivaDataResultOpen, setTivaDataResultOpen] = useState<boolean>(false);
    // const [tivaDataPayload, setTivaDataPayload] = useState<any>();

    const answerTime = 60000;
    const activeQuestionId = "activeQuestionId";
    const activeAnswerId = "activeAnswerId";
    const headerHeight = isTrial && isMobileDevice() ? 56 + 60 : 56;
    const footerHeight = 80;
    const messageSpace = 12;
    const messagePadding = 16;

    useEffect(() => {
        // searchParams.append("tivaDataOpen", tivaDataResultOpen.toString());
        // searchParams.set("tivaDataOpen", tivaDataResultOpen.toString());
        // history.replaceState(null, "", `?tivaDataOpen=${tivaDataResultOpen}`);

        setTivaDataResultOpen(!!searchParams.get("tivaDataPayload"));
    }, [searchParams]);

    const isActiveMessage = (message?: ChatMessage) => {
        return !!message?.id && message.id === activeMessage;
    };

    const scrollToBottom = (type: ScrollBehavior) => {
        messagesEndRef.current?.scrollIntoView({ behavior: type });
    };

    const scrollToReviewMessage = () => {
        reviewRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    };

    const typingMessage = (): ChatMessage => {
        return {
            text: "typing",
            isLoading: true,
            type: ChatBubbleType.typing,
            metadata: null
        };
    };

    const approveSentMessage = useCallback((uid: string, id: number) => {
        setMessages(prevMessages =>
            prevMessages
                .map(item =>
                    item.answer?.type === ChatBubbleType.typing
                        ? { ...item, answer: undefined }
                        : item
                )
                .map(item =>
                    item.question?.uid === uid
                        ? {
                              question: { ...item.question, id: id, isLoading: false },
                              answer: typingMessage()
                          }
                        : item
                )
        );
    }, []);

    // Question POST failed (e.g. 503 when the licensed question pool is
    // exhausted): stop the question spinner and show the server message as an
    // error bubble.
    const failSentMessage = useCallback((uid: string, text: string) => {
        setMessages(prevMessages =>
            prevMessages
                .map(item =>
                    item.answer?.type === ChatBubbleType.typing
                        ? { ...item, answer: undefined }
                        : item
                )
                .map(item =>
                    item.question?.uid === uid
                        ? {
                              question: { ...item.question, isLoading: false },
                              answer: {
                                  text,
                                  isLoading: false,
                                  type: ChatBubbleType.error,
                                  metadata: null
                              }
                          }
                        : item
                )
        );
    }, []);

    const setHistoryMessages = useCallback((items: Message[]) => {
        setMessages(
            items
                .filter(item => item.question || item.answer)
                .map((item, index) => {
                    const question = item.question
                        ? {
                              id: item.id,
                              text: item.question,
                              isLoading: false,
                              type: ChatBubbleType.question,
                              metadata: null
                          }
                        : undefined;
                    const answer =
                        item.answer || item.payload
                            ? {
                                  id: item.id,
                                  // P4-01c: reloaded suspended turns keep the
                                  // question-only bubble; chips re-render if
                                  // this is still the latest turn.
                                  text:
                                      askUserFromPayload(item.payload)?.question ??
                                      answerText(item as SseMessage),
                                  data: item.payload?.result,
                                  isLoading: false,
                                  type: answerType(item as SseMessage),
                                  metadata: item.metadata,
                                  // Persisted answer language for reloaded
                                  // history (WCAG 3.1.2).
                                  lang: (item.payload as any)?.output_language,
                                  askUser: askUserFromPayload(item.payload)
                              }
                            : index === items.length - 1 &&
                                new Date().getTime() - new Date(item.createdAt).getTime() <
                                    answerTime
                              ? typingMessage()
                              : undefined;

                    return { question, answer };
                })
                .reverse()
        );
    }, []);

    useEffect(() => {
        if (availableScrollDownType.current) {
            scrollToBottom(availableScrollDownType.current);
            availableScrollDownType.current = null;
        }
    }, [messages]);

    useEffect(() => {
        const handleStorageEventListener = (event: WindowEventMap["storage"]) => {
            switch (event.key) {
                case ChatEvents.QUESTION: {
                    const chatEvent = objGetter(ChatEvents.QUESTION) as QuestionEvent;
                    if (chatEvent?.chatId === chatId) {
                        availableScrollDownType.current = "smooth";
                        setMessages(prevMessages => [
                            ...prevMessages.map(item =>
                                item.answer?.type === ChatBubbleType.typing
                                    ? { ...item, answer: undefined }
                                    : item
                            ),
                            {
                                question: { ...chatEvent.message, isLoading: false },
                                answer: typingMessage()
                            }
                        ]);
                    }
                    break;
                }
            }
        };

        window.addEventListener("storage", handleStorageEventListener);

        return () => {
            window.removeEventListener("storage", handleStorageEventListener);
        };
    }, [chatId]);

    useEffect(() => {
        if (newMessage && user && chatId) {
            const messageUid = crypto.randomUUID();
            const message = {
                uid: messageUid,
                text: newMessage,
                isLoading: true,
                type: ChatBubbleType.question,
                metadata: null
            };
            availableScrollDownType.current = "smooth";
            setMessages(prevMessages => [...prevMessages, { question: message }]);

            setNewMessage(null);

            addQuestion(user.uid, chatId, { uuid: messageUid, question: newMessage })
                .then(response => {
                    approveSentMessage(messageUid, response.data.messageId);
                    setDataToLocalStorage(ChatEvents.QUESTION, {
                        chatId: chatId,
                        message: message
                    });
                })
                .catch(error => {
                    const serverMessage =
                        error?.response?.data?.message ||
                        "The server had an error. Try again later";
                    failSentMessage(messageUid, serverMessage);
                });
        }
    }, [newMessage, user, chatId, setNewMessage, approveSentMessage, failSentMessage]);

    useEffect(() => {
        if (!user || !chatId) return;

        if (!location.state?.isNew || !stringGetter("createNewChatKey")) {
            setIsLoading(true);
            fetchChatHistory(user.uid, chatId).then(response => {
                availableScrollDownType.current = "auto";
                setHistoryMessages(response.data.items);
                setIsLoading(false);
            });
        } else {
            removeDataToLocalStorage("createNewChatKey");
        }
        // Reset any open review when navigating to another chat. We inline the
        // state reset (rather than call closeReview) because closeReview's
        // focus-restore step targets the review's own close button, which is
        // meaningless on navigation — and depending on the non-memoized
        // closeReview here would re-run this fetch effect every render.
        setActiveMessage(undefined);
        setReviewScroll(false);
    }, [user, chatId, location.state, setHistoryMessages, setActiveMessage, setReviewScroll]);

    const answerText = (message: SseMessage) => {
        return message.answer
            ? message.answer
            : message.error
              ? (message.error?.message ?? "The server had an error. Try again later")
              : message.payload?.error
                ? `The server had an error. Try again later (Response Data):\n- type: **error**\n- message: **${message.payload?.error?.message ?? "none"}**`
                : message.payload?.result?.payload === "Something went wrong"
                  ? "The server had an error. Try again later (Response Data):\n- type: **text**\n- message: **Something went wrong**"
                  : message.payload
                    ? `Showing “${message.question}"`
                    : "The server had an error. Try again later";
    };

    const answerType = (message: SseMessage) => {
        return message.error ||
            message.payload?.error ||
            message.payload?.result?.payload === "Something went wrong"
            ? ChatBubbleType.error
            : message.payload
              ? ChatBubbleType.tivaData
              : message.question
                ? ChatBubbleType.answer
                : ChatBubbleType.summary;
    };

    // P3-06: activity feeds keyed by message id. A ref (not state) because
    // agent_started can arrive before the answer bubble exists — the feed is
    // stamped onto the bubble when it is created (below) and re-stamped by
    // updateActivityFeed on every later feed event.
    const activityFeedsRef = useRef<Record<number, ActivityFeed>>({});

    const handleAnswer = useCallback((message: SseMessage) => {
        if (!message.id && !message.error) return;

        if (!message.isStreaming) {
            console.log("New message", message);
        }

        setMessages(prevMessages => {
            const list = prevMessages.map(item =>
                item.answer?.type === ChatBubbleType.typing /// TODO can/will be remove
                    ? { ...item, answer: undefined }
                    : item
            );
            const currentMessage = prevMessages.find(
                item =>
                    item.answer?.id === message.id &&
                    (item.answer?.type === ChatBubbleType.answer ||
                        item.answer?.type === ChatBubbleType.summary ||
                        item.answer?.type === ChatBubbleType.error)
            );
            if (currentMessage) {
                return list.map(item => {
                    if (item.answer?.id === message.id) {
                        const text =
                            message.isStreaming &&
                            !message.isRetry &&
                            item.answer.answerId === message.answerId
                                ? item.answer.text + message.answer
                                : answerText(message);
                        return !item.answer.isLoading
                            ? item
                            : message.isWaiting
                              ? {
                                    ...item,
                                    answer: {
                                        ...item.answer,
                                        isLoading: message.isStreaming,
                                        answerId: message.answerId,
                                        text
                                    }
                                }
                              : {
                                    ...item,
                                    answer: {
                                        ...item.answer,
                                        // P4-01c: a suspended turn's bubble shows the
                                        // question alone (chips carry the options); the
                                        // persisted numbered-text fallback is for
                                        // surfaces without chips.
                                        text: askUserFromPayload(message.payload)?.question ?? text,
                                        data: message.payload?.result,
                                        isLoading: false,
                                        answerId: message.answerId,
                                        type: answerType(message),
                                        metadata: message.metadata,
                                        lang: message.languageCode,
                                        // Agentic path extras (P2-09)
                                        bestEffort: message.payload?.flags?.bestEffort,
                                        toolErrors: message.payload?.flags?.toolErrors,
                                        askUser: askUserFromPayload(message.payload)
                                    }
                                };
                    }
                    return item;
                });
            } else if (!!message.answer || !!message.payload || !!message.error) {
                const askUser = askUserFromPayload(message.payload);
                const answer = {
                    id: message.id,
                    answerId: message.answerId,
                    // P4-01c: question text alone on suspended turns — the
                    // chips carry the options.
                    text: askUser?.question ?? answerText(message),
                    data: message.payload?.result,
                    isLoading: message.isStreaming,
                    type: answerType(message),
                    metadata: message.metadata,
                    lang: message.languageCode,
                    // Agentic path extras (P2-09/P3-06)
                    activityFeed: message.id ? activityFeedsRef.current[message.id] : undefined,
                    bestEffort: message.payload?.flags?.bestEffort,
                    toolErrors: message.payload?.flags?.toolErrors,
                    askUser
                };

                if (list.find(item => item.question?.id === message.id)) {
                    return list.map(item =>
                        item.question?.id === message.id
                            ? {
                                  ...item,
                                  answer: answer
                              }
                            : item
                    );
                } else {
                    list.push({ answer: answer });
                    return list;
                }
            }
            return prevMessages;
        });
    }, []);

    // Agentic typed-event opt-in (plan P1-08, minimal plumbing). When the
    // flag is on, the SSE connection requests typed events and the handler
    // below translates token/status/reset into the legacy accumulate/replace
    // semantics handleAnswer already implements; richer events (citation,
    // tool_call, agent_started, handoff, ask_user) are parsed and logged —
    // their UI lands in later phases.
    const typedEventsEnabled = localStorage.getItem("agentic.typedEvents") !== "0";

    // P3-06: fold a feed-relevant event into the message's activity feed and
    // re-stamp the bubble (no-op when the bubble does not exist yet — the
    // feed is attached at bubble creation from activityFeedsRef).
    const updateActivityFeed = useCallback((id: number | undefined, event: FeedEvent) => {
        if (!id) return;
        const current = activityFeedsRef.current[id] ?? emptyActivityFeed();
        const next = applyActivityEvent(current, event);
        if (next === current) return;
        activityFeedsRef.current[id] = next;
        setMessages(prev =>
            prev.map(item =>
                item.answer?.id === id
                    ? { ...item, answer: { ...item.answer, activityFeed: next } }
                    : item
            )
        );
    }, []);

    const handleTypedEvent = useCallback(
        (message: SseMessage) => {
            const event = message.event;
            if (!event) return;
            const base = {
                ...message,
                event: undefined,
                isWaiting: true,
                isStreaming: true
            };
            switch (event.type) {
                case "token":
                    // constant answerId per message → chunks append
                    handleAnswer({
                        ...base,
                        answer: event.text ?? "",
                        answerId: `typed-${message.id}`
                    });
                    break;
                case "status":
                    // fresh answerId → replaces previous status/partial text;
                    // also recorded as a feed row (P3-06)
                    handleAnswer({
                        ...base,
                        answer: event.text ?? "",
                        answerId: `status-${Date.now()}`
                    });
                    updateActivityFeed(message.id, event as FeedEvent);
                    break;
                case "reset":
                    // failover: clear the partial answer (replace with empty)
                    handleAnswer({ ...base, answer: "", answerId: `reset-${Date.now()}` });
                    break;
                case "tool_call":
                case "agent_started":
                case "handoff":
                    // Grouped activity feed (P3-06): agent_started opens a
                    // group, handoff renders a transition, tool_call adds a
                    // row — semantics live in chat-surface applyActivityEvent.
                    updateActivityFeed(message.id, event as FeedEvent);
                    break;
                case "error":
                    console.error("agentic stream error event", event);
                    break;
                case "done":
                    // collapses the feed (disclosure toggle)
                    updateActivityFeed(message.id, event as FeedEvent);
                    console.debug("agentic turn done", event);
                    break;
                default:
                    // citation: UI arrives in a later phase. ask_user is
                    // payload-driven (P4-01c): the chips render when the
                    // final awaiting_user message lands, which follows the
                    // event within the same turn — no event handling needed.
                    console.debug("agentic event", event.type, event);
            }
        },
        [handleAnswer, updateActivityFeed]
    );

    useEffect(() => {
        if (!currentAccessToken || !user || !chatId) return;

        const eventSource = listenAnswers(user.uid, chatId, typedEventsEnabled);

        eventSource.onopen = () => {
            console.log("~~~ event source opend ~~~");
        };

        eventSource.onmessage = ({ data }) => {
            const message: SseMessage = JSON.parse(data);
            if (message.event) {
                handleTypedEvent(message);
            } else {
                handleAnswer(message);
            }
        };

        eventSource.onerror = () => {
            console.log("~~~ error when listening to event source ~~~");
        };

        return () => {
            console.log("~~~ event source closed ~~~");
            eventSource.close();
        };
    }, [currentAccessToken, user, chatId, handleAnswer, handleTypedEvent, typedEventsEnabled]);

    const openTivaDataResult = (data: any) => {
        if (isStatisticsReady) {
            // setTivaDataPayload(data?.payload);
            // setTivaDataResultOpen(true);
            navigate(`${location.pathname}?tivaDataPayload=${JSON.stringify(data?.payload)}`);
        } else {
            showToast(`Statistics service is not ready. MBA Analytics unavailable.`);
        }
    };

    const closeTivaDataResult = () => {
        navigate(location.pathname);
        // setTivaDataResultOpen(false);
        // setTivaDataPayload(undefined);
    };

    const handleSummarize = (summaryData: SummaryProps) => {
        if (user && chatId) {
            summarizeVideo(user.uid, chatId, summaryData).then(() => {
                availableScrollDownType.current = "smooth";
                setMessages(prevMessages => [...prevMessages, { answer: typingMessage() }]);
            });
        }
    };

    // Sheet excerpt viewer (spreadsheet citations design §4.3): one viewer
    // instance; a sheet-locator citation click fetches the cited sheet's
    // windowed rows and opens the modal.
    const [sheetViewer, setSheetViewer] = useState<{
        citation: any;
        excerpt: any;
        loading: boolean;
        error: string | null;
    } | null>(null);

    const handleLink = (metadata: any) => {
        if (metadata.playUrl) {
            setTimeout(() => {
                window.open(metadata.playUrl, "_blank");
            });
        } else if (user?.uid && metadata.messageId && metadata.bucket && metadata.bucketFilePath) {
            // Message-validated presign: the server confirms this file is a
            // citation of the caller's message before signing.
            resolveChatPresignedUrl(user.uid, metadata.messageId, {
                bucket: metadata.bucket,
                bucketFilePath: metadata.bucketFilePath,
                s3Service: metadata.s3Service
            }).then(response => {
                const url = response?.data?.url;
                if (url) {
                    const isMedia =
                        metadata.source_type === SourceType.video ||
                        metadata.source_type === SourceType.mp4 ||
                        metadata.source_type === SourceType.mp3 ||
                        metadata.source_type === SourceType.m4a;
                    let fragment = "";
                    if (metadata.source_type === SourceType.pdf && !!metadata.currentPage) {
                        fragment = `#page${isIOS() ? "" : "="}${metadata.currentPage}`;
                    } else if (isMedia && Number.isFinite(metadata.currentTime)) {
                        // HTML5 media fragment: #t=<seconds>. Honored by Chrome/Firefox/Safari
                        // (desktop + iOS) on direct MP4/MP3 URLs. iOS Safari opens media in
                        // QuickTime Player which respects the fragment. Stick to start-only —
                        // start,end ranges are inconsistently supported across mobile browsers.
                        fragment = `#t=${Math.max(0, Math.floor(metadata.currentTime))}`;
                    }
                    setTimeout(() => {
                        window.open(`${url}${fragment}`, "_blank");
                    });
                }
            });
        }
    };

    const handleSheetView = (citation: any) => {
        const meta = citation?.meta || {};
        if (
            !user?.uid ||
            !citation?.messageId ||
            !citation?.recognition_result_id ||
            !meta.sheet_name
        ) {
            // Not enough to address the excerpt (e.g. pre-Phase-1 payloads):
            // fall back to opening the whole file.
            handleLink(citation);
            return;
        }
        setSheetViewer({ citation, excerpt: null, loading: true, error: null });
        resolveSheetExcerpt(user.uid, citation.messageId, {
            recognitionResultId: citation.recognition_result_id,
            sheetName: meta.sheet_name,
            rowStart: typeof meta.row_start === "number" ? meta.row_start : 0,
            rowEnd: typeof meta.row_end === "number" ? meta.row_end : 0
        })
            .then(response =>
                setSheetViewer(viewer =>
                    viewer ? { ...viewer, excerpt: response?.data ?? null, loading: false } : viewer
                )
            )
            .catch(() =>
                setSheetViewer(viewer =>
                    viewer
                        ? {
                              ...viewer,
                              loading: false,
                              error: 'This sheet couldn’t be loaded. Try "Open file" instead.'
                          }
                        : viewer
                )
            );
    };

    const getReviewValue = (feedback?: FeedbackProps) => {
        return feedback?.answerRate === "1"
            ? Feedback.like
            : feedback?.answerRate === "-1"
              ? Feedback.dislike
              : Feedback.noReview;
    };

    const handleReview = (id: number, feedback: Feedback) => {
        switch (feedback) {
            case Feedback.like:
                submitReview("1", id, "");
                break;
            case Feedback.noReview:
                submitReview("0", id, "");
                break;
            case Feedback.dislike:
                submitReview("-1", id, "");
                openReview(id);
                break;
        }
    };

    const submitReview = (value: string, messageId?: number, comment?: string) => {
        if (!messageId || !chatId || !user?.uid) return;

        feedback(user.uid, chatId, {
            messageId: messageId,
            questionContext: "0",
            answerRate: value,
            answerRateComment: comment ?? ""
        });

        updateReview(messageId, value, comment ?? "");
    };

    const updateReview = (id: number, value: string, comment: string) => {
        setMessages(prevMessages =>
            prevMessages.map(item =>
                item.answer?.id === id
                    ? {
                          ...item,
                          answer: {
                              ...item.answer,
                              feedback: {
                                  questionContext: "0",
                                  answerRate: value,
                                  answerRateComment: comment
                              }
                          }
                      }
                    : item
            )
        );
    };

    const openReview = (id: number) => {
        // const chat = document.getElementById("chat-messages");
        // const scroll = document.getElementById("chat-scroll");
        // if (scroll && chat && scroll.clientHeight === chat.clientHeight) {
        //     chat.style.minHeight = "200%";
        //     scroll.scrollTop = chat.clientHeight / 2 - 359 - 32;
        // }

        setActiveMessage(id);
        setTimeout(() => {
            scrollToReviewMessage();
            setTimeout(() => {
                updateReviewScroll();
            }, 500);
        });
    };

    const closeReview = () => {
        // Which answer's review is closing — used to restore focus below.
        const answerId = activeMessage;

        setActiveMessage(undefined);
        setReviewScroll(false);

        // Return focus to the thumbs-down button that opened the review
        // (WCAG 2.4.3). The button node is re-created across the review's
        // open/close re-renders, so we can't hold a reference to it — instead
        // re-find the live button by its answer's stable id after the dialog
        // has unmounted (rAF defers past the browser's focus-to-<body> reset).
        if (answerId != null) {
            requestAnimationFrame(() => {
                const answerEl = document.getElementById(`answer-${answerId}`);
                const button = answerEl?.querySelector<HTMLElement>(
                    'button[aria-label="thumbs down"]'
                );
                button?.focus();
            });
        }

        // const chat = document.getElementById("chat-messages");
        // if (chat) {
        //     chat.style.minHeight = "100%";
        // }
    };

    const updateReviewScroll = () => {
        const activeQuestion = document.getElementById(activeQuestionId);
        const activeAnswer = document.getElementById(activeAnswerId);
        const scroll = document.getElementById("chat-scroll");
        if (!!activeQuestion && !!activeAnswer && !!scroll) {
            const reviewHeight = activeQuestion.clientHeight + activeAnswer.clientHeight + 32;
            setReviewScrollTop(activeQuestion.offsetTop - 16);
            setReviewHeight(reviewHeight);
            setReviewScroll(scroll.clientHeight < reviewHeight);
        } else {
            setReviewScroll(false);
        }
    };

    const handleReviewScroll = (event: UIEvent<HTMLElement>) => {
        if (!activeMessage) {
            const {
                scrollTop,
                scrollHeight,
                clientHeight
            }: { scrollTop: number; scrollHeight: number; clientHeight: number } =
                event.currentTarget;
            setScrolledDown(scrollHeight - scrollTop - clientHeight <= 500);
        } else if (activeMessage && reviewScroll) {
            const { scrollTop, clientHeight }: { scrollTop: number; clientHeight: number } =
                event.currentTarget;
            if (scrollTop < reviewScrollTop) {
                event.currentTarget.scrollTop = reviewScrollTop;
            } else if (scrollTop > reviewScrollTop + reviewHeight - clientHeight) {
                event.currentTarget.scrollTop = reviewScrollTop + reviewHeight - clientHeight;
            }
        }
    };

    const getMessageStyle = (index: number) => {
        if (messages.length - 1 !== index || !chatScrollRef.current) return {};

        return {
            minHeight: `${chatScrollRef.current.clientHeight - footerHeight - (headerHeight + messageSpace)}px`
        };
    };

    const changeExpanded = (value: boolean, questionId?: number) => {
        if (!questionId) return;

        setMessages(prevMessages =>
            prevMessages.map(item =>
                item.question?.id === questionId
                    ? { ...item, question: { ...item.question, isExpanded: value } }
                    : item
            )
        );
    };

    return (
        <div className="chat-messages-content">
            <div
                ref={chatScrollRef}
                id="chat-scroll"
                className="chat-messages-scroll"
                style={{ overflowY: activeMessage && !reviewScroll ? "hidden" : "auto" }}
                onScroll={handleReviewScroll}
            >
                <div
                    id="chat-messages"
                    className={`chat-messages${isTrial && isMobileDevice() ? " chat-messages--trial-mobile" : ""}`}
                >
                    {messages.length > 0 &&
                        messages.map((message, index) => (
                            <div
                                key={getMessagePairKey(message, index)}
                                style={getMessageStyle(index)}
                                className="chat-bot-result-pair"
                            >
                                {message.question && (
                                    <div
                                        id={
                                            isActiveMessage(message.question)
                                                ? activeQuestionId
                                                : `question-${getMessagePairKey(message, index)}`
                                        }
                                        className={`chat-bot-result--question ${isActiveMessage(message.question) ? "chat-bot-result--active" : ""}`}
                                        style={
                                            isMobileDevice()
                                                ? {
                                                      maxWidth: `${width - messagePadding * 2 - 30}px`
                                                  }
                                                : {}
                                        }
                                    >
                                        <ChatBubble
                                            id={message.question.id}
                                            message={message.question.text}
                                            isLoading={message.question.isLoading}
                                            type={message.question.type}
                                            isActive={isActiveMessage(message.question)}
                                            hideAvatars={true}
                                            isExpanded={message.question.isExpanded}
                                            setExpanded={(value: boolean) =>
                                                changeExpanded(value, message.question?.id)
                                            }
                                        />
                                    </div>
                                )}
                                {message.answer && (
                                    <div
                                        id={
                                            isActiveMessage(message.answer)
                                                ? activeAnswerId
                                                : `answer-${message.answer.id}`
                                        }
                                        className={`chat-bot-result--answer ${isActiveMessage(message.answer) ? "chat-bot-result--active" : ""}`}
                                        style={
                                            isMobileDevice()
                                                ? {
                                                      maxWidth: `${width - messagePadding * 2 - 30}px`
                                                  }
                                                : {}
                                        }
                                    >
                                        <ChatBubble
                                            id={message.answer.id}
                                            message={message.answer.text}
                                            isLoading={message.answer.isLoading}
                                            type={message.answer.type}
                                            data={message.answer.data}
                                            metadata={message.answer.metadata}
                                            lang={message.answer.lang}
                                            activityFeed={message.answer.activityFeed}
                                            bestEffort={message.answer.bestEffort}
                                            toolErrors={message.answer.toolErrors}
                                            handleLink={handleLink}
                                            handleSheetView={handleSheetView}
                                            handleTivaDataResult={openTivaDataResult}
                                            handleSummarize={handleSummarize}
                                            handleReview={handleReview}
                                            isActive={isActiveMessage(message.answer)}
                                            review={getReviewValue(message.answer.feedback)}
                                            showReview={true}
                                            showSources={true}
                                            showSourcesQuotes={false}
                                            showSummary={devMode}
                                            hideAvatars={true}
                                        />
                                        {message.answer.askUser?.options &&
                                            message.answer.askUser.options.length > 0 &&
                                            index === messages.length - 1 && (
                                                <AskUserOptions
                                                    options={message.answer.askUser.options}
                                                    onSelect={label => setNewMessage(label)}
                                                    // The options are what the agent's evidence
                                                    // surfaced, not the universe of answers —
                                                    // "Something else…" hands focus to the chat
                                                    // input (free text is always a valid reply).
                                                    onOther={() =>
                                                        document
                                                            .querySelector<HTMLTextAreaElement>(
                                                                ".footer-text-input textarea"
                                                            )
                                                            ?.focus()
                                                    }
                                                />
                                            )}
                                        {isActiveMessage(message.answer) && (
                                            <div
                                                ref={reviewRef}
                                                className={
                                                    isMobileDevice()
                                                        ? "chat-review--mobile"
                                                        : "chat-review"
                                                }
                                            >
                                                <ChatMessageReview
                                                    closeAction={closeReview}
                                                    submitAction={(comment: string) =>
                                                        submitReview("-1", activeMessage, comment)
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    {messages.length > 0 && <div ref={messagesEndRef} />}
                    {messages.length === 0 && (
                        <SvgIcon
                            component={placeholder}
                            className="chat-placeholder"
                            inheritViewBox
                        />
                    )}
                </div>
            </div>
            <IconButton
                className={`chat-messages-scroll-button${isScrolledDown ? " chat-messages-scroll-button--hidden" : ""}`}
                aria-label="Scroll to latest messages"
                onClick={() => scrollToBottom("smooth")}
            >
                <SvgIcon component={ArrowExpandIcon} inheritViewBox />
            </IconButton>
            <LoaderView show={isLoading} />
            <ChatMessageReviewBackdrop
                onClick={closeReview}
                open={!!activeMessage}
            ></ChatMessageReviewBackdrop>
            <Modal
                className="tiva-data-modal"
                open={tivaDataResultOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <div className="tiva-data-container">
                    <TivaDataModalView handleClose={closeTivaDataResult} />
                </div>
            </Modal>
            <SheetViewer
                open={!!sheetViewer}
                sourceName={
                    sheetViewer?.citation?.source ?? sheetViewer?.citation?.title ?? "Source"
                }
                excerpt={sheetViewer?.excerpt ?? null}
                loading={sheetViewer?.loading}
                error={sheetViewer?.error}
                onClose={() => setSheetViewer(null)}
                onOpenFile={sheetViewer ? () => handleLink(sheetViewer.citation) : undefined}
                liveSheet={
                    sheetViewer?.citation?.liveUrl
                        ? {
                              url: sheetViewer.citation.liveUrl,
                              // Google needs the gid; SharePoint/OneDrive links
                              // address by sheet name instead.
                              gid: sheetViewer.citation.meta?.sheet_gid
                          }
                        : undefined
                }
            />
        </div>
    );
};
